import { Button, cn, SurfaceDetailData, SurfaceInspectButton, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Badge } from './chunk-UCBSVPDO.mjs';
import { useState, useEffect, useMemo } from 'react';
import { CircleAlert, Info, ArrowUpRight } from 'lucide-react';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, ReferenceLine, Area, Line, BarChart, Bar, PieChart, Pie, Cell, LineChart, RadialBarChart, PolarAngleAxis, RadialBar } from 'recharts';

// src/charts/chart-theme.ts
var CHART_COLOR = {
  primary: "hsl(var(--context-primary))",
  "context-primary": "hsl(var(--context-primary))",
  brand: "hsl(var(--brand))",
  "brand-gold": "hsl(var(--brand-gold))",
  success: "hsl(var(--success))",
  warning: "hsl(var(--warning))",
  critical: "hsl(var(--critical))",
  info: "hsl(var(--info))",
  ai: "hsl(var(--ai))",
  muted: "hsl(var(--muted-foreground))",
  accent: "hsl(var(--ai))"
};
var CHART_SERIES_ORDER = [
  "primary",
  "success",
  "warning",
  "accent",
  "brand",
  "ai",
  "critical",
  "info",
  "muted",
  "context-primary",
  "brand-gold"
];
var CHART_CHROME = {
  grid: "hsl(0 0% 100% / 0.07)",
  axis: "hsl(var(--muted-foreground))",
  reference: "hsl(var(--context-primary) / 0.55)",
  cursor: "hsl(var(--context-primary) / 0.09)"
};
function areaFill(token) {
  return CHART_COLOR[token].replace(/\)$/, " / 0.10)");
}
function barFill(token) {
  return CHART_COLOR[token];
}
function seriesColor(index, override) {
  if (override) return CHART_COLOR[override];
  return CHART_COLOR[CHART_SERIES_ORDER[index % CHART_SERIES_ORDER.length]];
}
function seriesToken(index, override) {
  return override ?? CHART_SERIES_ORDER[index % CHART_SERIES_ORDER.length];
}
function patternId(token) {
  return "helios-chart-pattern-" + token;
}
function patternFill(token) {
  return "url(#" + patternId(token) + ")";
}
var AXIS_TICK = {
  fill: CHART_CHROME.axis,
  fontSize: 10,
  fontFamily: "var(--font-mono)"
};
function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(query.matches);
    const listener = (event) => setReduced(event.matches);
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", listener);
      return () => query.removeEventListener("change", listener);
    }
    return void 0;
  }, []);
  return reduced;
}
function ChartPatternDefs() {
  return /* @__PURE__ */ jsxs("defs", { children: [
    CHART_SERIES_ORDER.map((token, index) => /* @__PURE__ */ jsxs(
      "pattern",
      {
        id: patternId(token),
        patternUnits: "userSpaceOnUse",
        width: "6",
        height: "6",
        patternTransform: `rotate(${index % 4 * 45})`,
        children: [
          /* @__PURE__ */ jsx("rect", { width: "6", height: "6", fill: CHART_COLOR[token], opacity: 0.94 }),
          /* @__PURE__ */ jsx(
            "line",
            {
              x1: "0",
              y1: "0",
              x2: "0",
              y2: "6",
              stroke: "hsl(0 0% 100%)",
              strokeOpacity: index % 2 === 0 ? 0.22 : 0.14,
              strokeWidth: index % 2 === 0 ? 1.6 : 1
            }
          )
        ]
      },
      token
    )),
    CHART_SERIES_ORDER.map((token) => /* @__PURE__ */ jsxs(
      "linearGradient",
      {
        id: `helios-chart-bar-${token}`,
        x1: "0",
        y1: "0",
        x2: "0",
        y2: "1",
        children: [
          /* @__PURE__ */ jsx("stop", { offset: "0%", stopColor: CHART_COLOR[token], stopOpacity: 1 }),
          /* @__PURE__ */ jsx("stop", { offset: "100%", stopColor: CHART_COLOR[token], stopOpacity: 0.55 })
        ]
      },
      `bar-${token}`
    )),
    CHART_SERIES_ORDER.map((token) => /* @__PURE__ */ jsxs(
      "linearGradient",
      {
        id: `helios-chart-area-${token}`,
        x1: "0",
        y1: "0",
        x2: "0",
        y2: "1",
        children: [
          /* @__PURE__ */ jsx("stop", { offset: "0%", stopColor: CHART_COLOR[token], stopOpacity: 0.5 }),
          /* @__PURE__ */ jsx("stop", { offset: "65%", stopColor: CHART_COLOR[token], stopOpacity: 0.12 }),
          /* @__PURE__ */ jsx("stop", { offset: "100%", stopColor: CHART_COLOR[token], stopOpacity: 0 })
        ]
      },
      `area-${token}`
    ))
  ] });
}
function SeriesSwatch({ token }) {
  const index = CHART_SERIES_ORDER.indexOf(token);
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: "10",
      height: "10",
      viewBox: "0 0 6 6",
      "aria-hidden": "true",
      className: "shrink-0 rounded-[3px]",
      children: [
        /* @__PURE__ */ jsx("rect", { width: "6", height: "6", fill: CHART_COLOR[token], opacity: 0.94 }),
        /* @__PURE__ */ jsx(
          "line",
          {
            x1: "0",
            y1: "0",
            x2: "0",
            y2: "6",
            stroke: "hsl(0 0% 100%)",
            strokeOpacity: index % 2 === 0 ? 0.22 : 0.14,
            strokeWidth: index % 2 === 0 ? 1.6 : 1,
            transform: `rotate(${index % 4 * 45} 3 3)`
          }
        )
      ]
    }
  );
}
function tooltipNumber(value) {
  if (value === null || value === void 0) return null;
  if (Array.isArray(value)) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
function ChartTooltipCard({
  label,
  rows,
  footer
}) {
  return /* @__PURE__ */ jsxs("div", { className: "glass-strong rounded-xl px-3 py-2", children: [
    /* @__PURE__ */ jsx("p", { className: "text-foreground text-2xs font-semibold", children: label }),
    /* @__PURE__ */ jsx("ul", { className: "mt-1 space-y-0.5", children: rows.map((row) => /* @__PURE__ */ jsxs("li", { className: "flex items-center gap-2 text-2xs", children: [
      row.color ? /* @__PURE__ */ jsx(
        "span",
        {
          className: "h-2 w-2 shrink-0 rounded-full",
          style: { backgroundColor: row.color, boxShadow: `0 0 8px ${row.color}` },
          "aria-hidden": "true"
        }
      ) : null,
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: row.name }),
      /* @__PURE__ */ jsx("span", { className: "text-foreground ml-auto font-mono tabular-nums", children: row.value })
    ] }, row.key)) }),
    footer ? /* @__PURE__ */ jsx("p", { className: "text-muted-foreground mt-1 text-2xs", children: footer }) : null
  ] });
}
function ChartFrame({
  title,
  basis,
  description,
  availabilityMessage,
  state = "ok",
  emptyMessage,
  unavailableMessage,
  errorMessage,
  onRetry,
  action,
  legend,
  tableColumns,
  tableRows,
  tableSourceState,
  onDrill,
  drillVerb = "Break down",
  height = 220,
  className,
  bodyClassName,
  children
}) {
  const tableId = `chart-table-${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
  const tableIsCurrent = state !== "loading" && state !== "error" && (tableSourceState ?? (state === "ok" ? "current" : "withheld")) === "current";
  const currentTableRows = tableIsCurrent ? tableRows : [];
  const tableStateMessage = state === "loading" ? `Loading ${title}` : state === "error" ? `${title} could not be loaded${errorMessage ? `. ${errorMessage}` : ""}` : state === "unavailable" ? unavailableMessage ?? "Source figures are unavailable." : emptyMessage ?? "No records match this basis.";
  const detail = useMemo(
    () => ({
      title,
      description: basis,
      sections: tableIsCurrent ? [
        ...state !== "ok" ? [
          {
            title: "Source state",
            fields: [
              { label: "Plot availability", value: state },
              { label: "Basis", value: basis }
            ]
          }
        ] : [],
        {
          title: "Recorded chart data",
          columns: tableColumns,
          rows: tableRows.map((row) => row.cells)
        }
      ] : [
        {
          title: "Source state",
          fields: [
            { label: "Availability", value: state },
            { label: "Basis", value: basis }
          ]
        }
      ]
    }),
    [title, basis, state, tableColumns, tableRows, tableIsCurrent]
  );
  return /* @__PURE__ */ jsxs(
    "figure",
    {
      "data-data-surface": "chart",
      className: cn("glass hx-data-surface hx-rise flex min-w-0 flex-col rounded-lg", className),
      "aria-labelledby": `${tableId}-title`,
      children: [
        /* @__PURE__ */ jsxs("figcaption", { className: "hx-section-header flex flex-wrap items-start justify-between gap-2 px-4 py-2.5", children: [
          /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
            /* @__PURE__ */ jsx("h3", { id: `${tableId}-title`, className: "hx-section-title text-sm font-semibold", children: title }),
            /* @__PURE__ */ jsx("p", { className: "text-muted-foreground text-2xs", children: basis }),
            description ? /* @__PURE__ */ jsx("p", { className: "text-muted-foreground text-2xs mt-0.5", children: description }) : null,
            availabilityMessage && (state === "ok" || state === "unavailable") ? /* @__PURE__ */ jsx("p", { className: "text-muted-foreground text-2xs mt-0.5", children: availabilityMessage }) : null
          ] }),
          action
        ] }),
        /* @__PURE__ */ jsxs("div", { className: cn("min-w-0 flex-1 p-3", bodyClassName), children: [
          state === "loading" ? /* @__PURE__ */ jsx(
            "div",
            {
              className: "hx-shimmer rounded-lg",
              style: { height },
              role: "status",
              "aria-label": `Loading ${title}`
            }
          ) : state === "error" ? /* @__PURE__ */ jsxs(
            "div",
            {
              role: "alert",
              className: "border-critical/30 bg-critical/[0.06] flex flex-col items-center justify-center gap-2 rounded-lg border px-4 py-8 text-center",
              style: { minHeight: height },
              children: [
                /* @__PURE__ */ jsx(CircleAlert, { className: "text-critical h-5 w-5", "aria-hidden": "true" }),
                /* @__PURE__ */ jsxs("p", { className: "text-foreground text-xs font-medium", children: [
                  title,
                  " could not be loaded"
                ] }),
                errorMessage ? /* @__PURE__ */ jsx("p", { className: "text-muted-foreground text-2xs max-w-sm", children: errorMessage }) : null,
                onRetry ? /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", onClick: onRetry, children: "Retry" }) : null
              ]
            }
          ) : state === "empty" || state === "unavailable" ? /* @__PURE__ */ jsxs(
            "div",
            {
              "data-testid": state === "unavailable" ? "chart-unavailable" : "chart-empty",
              className: "flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-4 py-8 text-center",
              style: { minHeight: height },
              children: [
                /* @__PURE__ */ jsx(Info, { className: "text-muted-foreground h-5 w-5", "aria-hidden": "true" }),
                /* @__PURE__ */ jsx("p", { className: "text-foreground text-xs font-medium", children: state === "unavailable" ? "Data unavailable" : "Nothing to plot yet" }),
                /* @__PURE__ */ jsx("p", { className: "text-muted-foreground text-2xs max-w-sm", children: state === "unavailable" ? unavailableMessage ?? "Source figures are unavailable. Missing values have not been replaced with zero." : emptyMessage ?? "No records match this basis. Nothing has been drawn at zero to fill the space." })
              ]
            }
          ) : /* @__PURE__ */ jsx("div", { className: "min-w-0", style: { height }, children }),
          legend && legend.length > 0 && state === "ok" ? /* @__PURE__ */ jsx("ul", { className: "mt-2 flex flex-wrap items-center gap-x-3 gap-y-1", children: legend.map((entry) => /* @__PURE__ */ jsxs(
            "li",
            {
              className: "text-muted-foreground text-2xs flex items-center gap-1.5",
              children: [
                /* @__PURE__ */ jsx(SeriesSwatch, { token: entry.token }),
                entry.label
              ]
            },
            entry.key
          )) }) : null
        ] }),
        /* @__PURE__ */ jsx("div", { className: "focus-within:border-border focus-within:bg-popover sr-only focus-within:not-sr-only focus-within:m-3 focus-within:rounded-lg focus-within:border focus-within:p-3", children: /* @__PURE__ */ jsxs("table", { "data-testid": `${tableId}-data`, className: "w-full border-collapse text-left text-xs", children: [
          /* @__PURE__ */ jsxs("caption", { className: "text-muted-foreground text-2xs pb-2 text-left", children: [
            title,
            " \u2014 ",
            basis,
            tableIsCurrent && onDrill ? " \xB7 activate a row to break it down" : ""
          ] }),
          /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsx("tr", { children: tableColumns.map((column) => /* @__PURE__ */ jsx("th", { scope: "col", className: "text-muted-foreground px-2 py-1", children: column }, column)) }) }),
          /* @__PURE__ */ jsx("tbody", { children: currentTableRows.length === 0 ? /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx("td", { colSpan: tableColumns.length, className: "px-2 py-1", children: tableStateMessage }) }) : currentTableRows.map((row) => /* @__PURE__ */ jsxs("tr", { children: [
            /* @__PURE__ */ jsx("th", { scope: "row", className: "text-foreground px-2 py-1 font-normal", children: onDrill ? /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                onClick: () => onDrill(row.id),
                className: "focus-visible:ring-ring rounded underline focus-visible:outline-none focus-visible:ring-2",
                children: [
                  row.cells[0],
                  /* @__PURE__ */ jsxs("span", { className: "sr-only", children: [
                    " \u2014 ",
                    drillVerb
                  ] })
                ]
              }
            ) : row.cells[0] }),
            row.cells.slice(1).map((cell, index) => /* @__PURE__ */ jsx("td", { className: "px-2 py-1 tabular-nums", children: cell }, `${row.id}-${index}`))
          ] }, row.id)) })
        ] }) }),
        /* @__PURE__ */ jsx(SurfaceDetailData, { detail }),
        /* @__PURE__ */ jsx(SurfaceInspectButton, { title })
      ]
    }
  );
}

// src/charts/drill.ts
function cohortHasRecords(cohort) {
  return cohort.recordsAvailable !== false && cohort.records.length > 0;
}
function findCohort(cohorts, cohortId) {
  if (!cohortId) return null;
  return cohorts.find((cohort) => cohort.id === cohortId) ?? null;
}
function ChartDrillDown({
  cohort,
  onOpenChange,
  renderLink,
  className
}) {
  const open = cohort !== null;
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsx(
    DialogContent,
    {
      className: cn("max-h-[80vh] max-w-2xl overflow-hidden", className),
      "data-testid": "chart-drilldown",
      children: cohort ? /* @__PURE__ */ jsx(DrillBody, { cohort, renderLink }) : null
    }
  ) });
}
function DrillBody({
  cohort,
  renderLink
}) {
  const hasRecords = cohortHasRecords(cohort);
  const unavailable = cohort.recordsAvailable === false;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(DialogHeader, { children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-baseline justify-between gap-2 pr-8", children: [
        /* @__PURE__ */ jsx(DialogTitle, { children: cohort.label }),
        cohort.value ? /* @__PURE__ */ jsx(
          "span",
          {
            className: "text-foreground font-mono text-base tabular-nums",
            "data-testid": "drilldown-value",
            children: cohort.value
          }
        ) : null
      ] }),
      /* @__PURE__ */ jsxs(DialogDescription, { children: [
        /* @__PURE__ */ jsx("span", { "data-testid": "drilldown-measure", children: cohort.measure }),
        /* @__PURE__ */ jsx("span", { className: "text-muted-foreground/70", children: " \xB7 " }),
        /* @__PURE__ */ jsx("span", { "data-testid": "drilldown-basis", children: cohort.basis })
      ] })
    ] }),
    unavailable ? /* @__PURE__ */ jsxs(
      "div",
      {
        "data-testid": "drilldown-records-unavailable",
        className: "border-border bg-secondary/20 flex items-start gap-2 rounded-lg border border-dashed p-3",
        children: [
          /* @__PURE__ */ jsx(Info, { className: "text-muted-foreground mt-0.5 h-4 w-4 shrink-0", "aria-hidden": "true" }),
          /* @__PURE__ */ jsxs("div", { className: "min-w-0 space-y-1", children: [
            /* @__PURE__ */ jsx("p", { className: "text-foreground text-xs font-medium", children: "The records behind this slice are not available here." }),
            /* @__PURE__ */ jsx("p", { className: "text-muted-foreground text-2xs", children: cohort.recordsUnavailableReason ?? "This module does not yet expose the underlying rows for this measure. Nothing has been fabricated to fill the table." })
          ] })
        ]
      }
    ) : hasRecords ? /* @__PURE__ */ jsx("div", { className: "max-h-[45vh] overflow-y-auto", children: /* @__PURE__ */ jsxs("table", { className: "w-full border-collapse text-left text-xs", children: [
      /* @__PURE__ */ jsx("thead", { className: "bg-background/70 text-muted-foreground text-2xs sticky top-0 uppercase", children: /* @__PURE__ */ jsxs("tr", { children: [
        /* @__PURE__ */ jsx("th", { className: "px-2 py-1.5", children: "Record" }),
        /* @__PURE__ */ jsx("th", { className: "px-2 py-1.5", children: "Date" }),
        /* @__PURE__ */ jsx("th", { className: "px-2 py-1.5 text-right", children: "Amount" })
      ] }) }),
      /* @__PURE__ */ jsx("tbody", { "data-testid": "drilldown-records", children: cohort.records.map((record) => /* @__PURE__ */ jsx(RecordRow, { record, renderLink }, record.id)) })
    ] }) }) : /* @__PURE__ */ jsxs(
      "div",
      {
        "data-testid": "drilldown-records-empty",
        className: "border-border bg-secondary/20 flex items-start gap-2 rounded-lg border border-dashed p-3",
        children: [
          /* @__PURE__ */ jsx(Info, { className: "text-muted-foreground mt-0.5 h-4 w-4 shrink-0", "aria-hidden": "true" }),
          /* @__PURE__ */ jsx("p", { className: "text-muted-foreground text-2xs", children: "This cohort has no records on the stated basis. That is the real answer, not a loading state." })
        ]
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "border-border/60 mt-4 flex flex-wrap items-center justify-between gap-2 border-t pt-3", children: [
      /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "font-mono text-2xs", children: [
        cohort.records.length,
        " record",
        cohort.records.length === 1 ? "" : "s",
        " shown"
      ] }),
      cohort.viewAllHref ? /* @__PURE__ */ jsx("span", { className: "text-primary inline-flex items-center gap-1 text-xs font-medium", children: renderLink ? renderLink(
        cohort.viewAllHref,
        /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1 hover:underline", children: [
          cohort.viewAllLabel ?? "View all in the module",
          /* @__PURE__ */ jsx(ArrowUpRight, { className: "h-3 w-3", "aria-hidden": "true" })
        ] })
      ) : /* @__PURE__ */ jsxs(
        "a",
        {
          href: cohort.viewAllHref,
          className: "inline-flex items-center gap-1 hover:underline",
          children: [
            cohort.viewAllLabel ?? "View all in the module",
            /* @__PURE__ */ jsx(ArrowUpRight, { className: "h-3 w-3", "aria-hidden": "true" })
          ]
        }
      ) }) : null
    ] })
  ] });
}
function RecordRow({
  record,
  renderLink
}) {
  const title = /* @__PURE__ */ jsxs("span", { className: "min-w-0", children: [
    /* @__PURE__ */ jsx("span", { className: "text-foreground block truncate font-medium", children: record.title }),
    record.subtitle ? /* @__PURE__ */ jsx("span", { className: "text-muted-foreground block truncate text-2xs", children: record.subtitle }) : null
  ] });
  return /* @__PURE__ */ jsxs("tr", { className: "border-border/60 border-t", children: [
    /* @__PURE__ */ jsx("td", { className: "max-w-[26rem] px-2 py-1.5", children: record.href ? renderLink ? renderLink(
      record.href,
      /* @__PURE__ */ jsxs("span", { className: "hover:text-brand inline-flex min-w-0 items-center gap-1", children: [
        title,
        /* @__PURE__ */ jsx(
          ArrowUpRight,
          {
            className: "text-muted-foreground h-3 w-3 shrink-0",
            "aria-hidden": "true"
          }
        )
      ] })
    ) : /* @__PURE__ */ jsxs("a", { href: record.href, className: "hover:text-brand inline-flex min-w-0 items-center gap-1", children: [
      title,
      /* @__PURE__ */ jsx(ArrowUpRight, { className: "text-muted-foreground h-3 w-3 shrink-0", "aria-hidden": "true" })
    ] }) : title }),
    /* @__PURE__ */ jsx("td", { className: "text-muted-foreground px-2 py-1.5 whitespace-nowrap tabular-nums", children: record.date ?? "\u2014" }),
    /* @__PURE__ */ jsx("td", { className: "text-foreground px-2 py-1.5 text-right font-mono whitespace-nowrap tabular-nums", children: record.amount ?? "\u2014" })
  ] });
}

// src/charts/chart-format.ts
var EM_DASH = "\u2014";
function parseMoney(value) {
  if (value === null || value === void 0 || typeof value === "string" && value.trim() === "") return null;
  const amount = typeof value === "number" ? value : Number(value);
  return Number.isFinite(amount) ? amount : null;
}
function isChartNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}
function formatAvailableValue(value, format = "count") {
  return isChartNumber(value) ? formatValue(value, format) : "Unavailable";
}
function formatCurrency(value) {
  const amount = parseMoney(value);
  if (amount === null) return EM_DASH;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}
function formatCurrencyCompact(value) {
  const amount = parseMoney(value);
  if (amount === null) return EM_DASH;
  const magnitude = Math.abs(amount);
  if (magnitude >= 1e6) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 1
    }).format(amount);
  }
  if (magnitude >= 1e3) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 0
    }).format(amount);
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(amount);
}
function formatPercent(value, fractionDigits = 1) {
  if (value === null || value === void 0 || !Number.isFinite(value)) return EM_DASH;
  return `${value.toFixed(fractionDigits)}%`;
}
function formatCount(value) {
  if (value === null || value === void 0 || !Number.isFinite(value)) return EM_DASH;
  return new Intl.NumberFormat("en-US").format(value);
}
function formatHours(value) {
  const hours = parseMoney(value);
  if (hours === null) return EM_DASH;
  return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(hours)} h`;
}
function formatShortDate(value) {
  if (!value) return EM_DASH;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
}
function formatMonthLabel(value) {
  if (!value) return EM_DASH;
  const iso = /^\d{4}-\d{2}$/.test(value) ? `${value}-01T00:00:00.000Z` : value;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC"
  }).format(date);
}
function formatValue(value, format = "count") {
  switch (format) {
    case "currency":
      return formatCurrency(value);
    case "currencyCompact":
      return formatCurrencyCompact(value);
    case "percent":
      return formatPercent(parseMoney(value));
    case "decimal": {
      const decimal = parseMoney(value);
      return decimal === null ? EM_DASH : new Intl.NumberFormat("en-US", { maximumFractionDigits: 20 }).format(decimal);
    }
    case "hours":
      return formatHours(value);
    case "count":
    default:
      return formatCount(parseMoney(value));
  }
}
function shareOfTotal(value, total) {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total === 0) return null;
  const share = value / total * 100;
  return Number.isFinite(share) ? share : null;
}
function TrendChart({
  points,
  series,
  format = "currencyCompact",
  reference,
  onDrill,
  state,
  ...frame
}) {
  const rows = useMemo(
    () => points.map((point) => ({
      id: point.id,
      label: point.label,
      ...Object.fromEntries(series.map((entry) => [entry.key, isChartNumber(point.values[entry.key]) ? point.values[entry.key] : null]))
    })),
    [points, series]
  );
  const labelById = useMemo(
    () => new Map(points.map((point) => [point.id, point.label])),
    [points]
  );
  const byId = useMemo(() => new Map(points.map((point) => [point.id, point])), [points]);
  const hasMeasuredValue = points.some((point) => series.some((entry) => isChartNumber(point.values[entry.key])));
  const hasMissing = points.some((point) => series.some((entry) => !isChartNumber(point.values[entry.key])));
  const resolvedState = state && state !== "ok" ? state : points.length === 0 ? "empty" : hasMeasuredValue ? "ok" : "unavailable";
  const tableColumns = ["Period", ...series.map((entry) => entry.label)];
  const tableRows = points.map((point) => ({
    id: point.id,
    cells: [
      point.label,
      ...series.map(
        (entry) => !isChartNumber(point.values[entry.key]) ? "no data" : formatValue(point.values[entry.key], format)
      )
    ]
  }));
  return /* @__PURE__ */ jsx(
    ChartFrame,
    {
      ...frame,
      state: resolvedState,
      tableSourceState: state === void 0 || state === "ok" ? "current" : "withheld",
      availabilityMessage: (state === void 0 || state === "ok") && hasMissing ? "Some source measurements are unavailable. Missing or invalid measurements remain gaps, not zeroes." : void 0,
      legend: series.map((entry, index) => ({
        key: entry.key,
        label: entry.label,
        token: seriesToken(index, entry.token)
      })),
      tableColumns,
      tableRows,
      onDrill,
      drillVerb: "see the records for this period",
      children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(
        ComposedChart,
        {
          data: rows,
          margin: { top: 6, right: 8, bottom: 0, left: 0 },
          onClick: (chartState) => {
            const id = chartState?.activeLabel;
            if (onDrill && typeof id === "string") onDrill(id);
          },
          children: [
            /* @__PURE__ */ jsx(ChartPatternDefs, {}),
            /* @__PURE__ */ jsx(CartesianGrid, { stroke: CHART_CHROME.grid, strokeDasharray: "2 4", vertical: false }),
            /* @__PURE__ */ jsx(
              XAxis,
              {
                dataKey: "id",
                tick: AXIS_TICK,
                tickLine: false,
                axisLine: { stroke: CHART_CHROME.grid },
                tickFormatter: (id) => labelById.get(id) ?? id,
                minTickGap: 12
              }
            ),
            /* @__PURE__ */ jsx(
              YAxis,
              {
                tick: AXIS_TICK,
                tickLine: false,
                axisLine: false,
                width: 62,
                tickFormatter: (value) => formatValue(value, format)
              }
            ),
            /* @__PURE__ */ jsx(
              Tooltip,
              {
                cursor: { stroke: CHART_CHROME.reference, strokeDasharray: "3 3" },
                content: ({
                  active,
                  payload,
                  label
                }) => {
                  if (!active || !payload?.length) return null;
                  const point = byId.get(String(label));
                  if (!point) return null;
                  return /* @__PURE__ */ jsx(
                    ChartTooltipCard,
                    {
                      label: labelById.get(String(label)) ?? String(label ?? ""),
                      rows: series.map((entry) => ({
                        key: entry.key,
                        name: entry.label,
                        value: !isChartNumber(point.values[entry.key]) ? "no data" : formatValue(point.values[entry.key], format),
                        color: payload.find((item) => item.dataKey === entry.key)?.color
                      })),
                      footer: onDrill ? "Click to break this period down" : void 0
                    }
                  );
                }
              }
            ),
            reference && isChartNumber(reference.value) ? /* @__PURE__ */ jsx(
              ReferenceLine,
              {
                y: reference.value,
                stroke: CHART_CHROME.reference,
                strokeDasharray: "4 4",
                label: {
                  value: reference.label,
                  position: "insideTopRight",
                  fill: CHART_CHROME.axis,
                  fontSize: 9
                }
              }
            ) : null,
            series.map(
              (entry, index) => entry.kind === "area" ? /* @__PURE__ */ jsx(
                Area,
                {
                  type: "monotone",
                  dataKey: entry.key,
                  name: entry.label,
                  className: `hx-glow-${seriesToken(index, entry.token)}`,
                  stroke: seriesColor(index, entry.token),
                  fill: areaFill(seriesToken(index, entry.token)),
                  strokeWidth: 2.5,
                  connectNulls: false,
                  isAnimationActive: false,
                  animationEasing: "ease-out",
                  dot: false,
                  activeDot: { r: 5, stroke: "hsl(var(--background))", strokeWidth: 2 }
                },
                entry.key
              ) : /* @__PURE__ */ jsx(
                Line,
                {
                  type: "monotone",
                  dataKey: entry.key,
                  name: entry.label,
                  className: `hx-glow-${seriesToken(index, entry.token)}`,
                  stroke: seriesColor(index, entry.token),
                  strokeWidth: 2.5,
                  strokeLinecap: "round",
                  connectNulls: false,
                  isAnimationActive: false,
                  animationEasing: "ease-out",
                  dot: false,
                  activeDot: { r: 5, stroke: "hsl(var(--background))", strokeWidth: 2 }
                },
                entry.key
              )
            )
          ]
        }
      ) })
    }
  );
}
function breakdownTotal(datum, series) {
  if (series.length === 0 || !series.every((entry) => isChartNumber(datum.values[entry.key]))) {
    return null;
  }
  const total = series.reduce((sum, entry) => sum + datum.values[entry.key], 0);
  return isChartNumber(total) ? total : null;
}
function BreakdownBar({
  data,
  series,
  format = "currencyCompact",
  axisFormat = format,
  stacked = true,
  layout = "vertical",
  onDrill,
  state,
  ...frame
}) {
  const horizontal = layout === "horizontal";
  const rows = useMemo(
    () => data.map((datum) => ({
      id: datum.id,
      label: datum.label,
      ...Object.fromEntries(
        series.map((entry) => [
          entry.key,
          isChartNumber(datum.values[entry.key]) ? datum.values[entry.key] : null
        ])
      )
    })),
    [data, series]
  );
  const labelById = useMemo(() => new Map(data.map((d) => [d.id, d.label])), [data]);
  const byId = useMemo(() => new Map(data.map((datum) => [datum.id, datum])), [data]);
  const complete = (datum) => breakdownTotal(datum, series) !== null;
  const hasMissing = data.some((datum) => !complete(datum));
  const plotted = stacked ? rows.filter((_row, index) => complete(data[index])) : rows;
  const hasPlottableValue = data.some(
    (datum) => stacked ? complete(datum) : series.some((entry) => isChartNumber(datum.values[entry.key]))
  );
  const resolvedState = state && state !== "ok" ? state : data.length === 0 ? "empty" : hasPlottableValue ? "ok" : "unavailable";
  const tableColumns = ["Category", ...series.map((entry) => entry.label), "Total"];
  const tableRows = data.map((datum) => {
    const total = breakdownTotal(datum, series);
    return {
      id: datum.id,
      cells: [
        datum.label,
        ...series.map((entry) => formatAvailableValue(datum.values[entry.key], format)),
        formatAvailableValue(total, format)
      ]
    };
  });
  const categoryAxis = /* @__PURE__ */ jsx(
    XAxis,
    {
      ...horizontal ? { type: "number", tickFormatter: (v) => formatValue(v, axisFormat) } : {
        type: "category",
        dataKey: "id",
        tickFormatter: (id) => labelById.get(id) ?? id
      },
      tick: AXIS_TICK,
      tickLine: false,
      axisLine: { stroke: CHART_CHROME.grid },
      interval: 0,
      angle: horizontal ? 0 : -20,
      textAnchor: horizontal ? "middle" : "end",
      height: horizontal ? 24 : 48
    }
  );
  const valueAxis = /* @__PURE__ */ jsx(
    YAxis,
    {
      ...horizontal ? {
        type: "category",
        dataKey: "id",
        tickFormatter: (id) => labelById.get(id) ?? id,
        width: 120
      } : {
        type: "number",
        tickFormatter: (v) => formatValue(v, axisFormat),
        width: 62
      },
      tick: AXIS_TICK,
      tickLine: false,
      axisLine: false
    }
  );
  return /* @__PURE__ */ jsx(
    ChartFrame,
    {
      ...frame,
      state: resolvedState,
      tableSourceState: state === void 0 || state === "ok" ? "current" : "withheld",
      availabilityMessage: (state === void 0 || state === "ok") && hasMissing ? `Some source figures are unavailable. ${stacked ? "Incomplete rows are not plotted as complete stacks." : "Only known values are plotted."} Totals require every series.` : void 0,
      legend: series.map((entry, index) => ({
        key: entry.key,
        label: entry.label,
        token: seriesToken(index, entry.token)
      })),
      tableColumns,
      tableRows,
      onDrill,
      drillVerb: "see the postings behind it",
      children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(
        BarChart,
        {
          data: plotted,
          layout: horizontal ? "vertical" : "horizontal",
          margin: { top: 6, right: 8, bottom: 0, left: 0 },
          onClick: (chartState) => {
            const id = chartState?.activeLabel;
            if (onDrill && typeof id === "string") onDrill(id);
          },
          children: [
            /* @__PURE__ */ jsx(ChartPatternDefs, {}),
            /* @__PURE__ */ jsx(
              CartesianGrid,
              {
                stroke: CHART_CHROME.grid,
                strokeDasharray: "2 4",
                vertical: horizontal,
                horizontal: !horizontal
              }
            ),
            categoryAxis,
            valueAxis,
            /* @__PURE__ */ jsx(
              Tooltip,
              {
                cursor: { fill: CHART_CHROME.cursor },
                content: ({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const datum = byId.get(String(label));
                  if (!datum) return null;
                  const total = breakdownTotal(datum, series);
                  return /* @__PURE__ */ jsx(
                    ChartTooltipCard,
                    {
                      label: labelById.get(String(label)) ?? String(label ?? ""),
                      rows: series.map((entry) => ({
                        key: entry.key,
                        name: entry.label,
                        value: formatAvailableValue(datum.values[entry.key], format),
                        color: payload.find((item) => item.dataKey === entry.key)?.color
                      })),
                      footer: `Total ${formatAvailableValue(total, format)}`
                    }
                  );
                }
              }
            ),
            series.map((entry, index) => /* @__PURE__ */ jsx(
              Bar,
              {
                dataKey: entry.key,
                name: entry.label,
                stackId: stacked ? "stack" : void 0,
                fill: patternFill(seriesToken(index, entry.token)),
                isAnimationActive: false,
                maxBarSize: horizontal ? 18 : 42,
                radius: stacked ? 0 : horizontal ? [0, 8, 8, 0] : [8, 8, 0, 0],
                cursor: onDrill ? "pointer" : void 0
              },
              entry.key
            ))
          ]
        }
      ) })
    }
  );
}
function ShareDonut({
  slices,
  format = "currencyCompact",
  centerValue,
  centerLabel = "Total",
  onDrill,
  state,
  height = 220,
  ...frame
}) {
  const complete = slices.every((slice) => isChartNumber(slice.value) && slice.value >= 0);
  const total = complete ? slices.reduce((sum, slice) => sum + slice.value, 0) : null;
  const compositionAvailable = complete && isChartNumber(total);
  const positive = useMemo(
    () => compositionAvailable ? slices.filter((slice) => isChartNumber(slice.value) && slice.value > 0) : [],
    [slices, compositionAvailable]
  );
  const resolvedState = state && state !== "ok" ? state : slices.length === 0 ? "empty" : !compositionAvailable ? "unavailable" : positive.length === 0 ? "empty" : "ok";
  const tableColumns = ["Slice", "Value", "Share"];
  const tableRows = slices.map((slice) => ({
    id: slice.id,
    cells: [
      slice.label,
      formatAvailableValue(slice.value, format),
      compositionAvailable && isChartNumber(slice.value) && total !== null && total > 0 ? formatPercent(shareOfTotal(slice.value, total)) : "Unavailable"
    ]
  }));
  return /* @__PURE__ */ jsx(
    ChartFrame,
    {
      ...frame,
      height,
      state: resolvedState,
      tableSourceState: state === void 0 || state === "ok" ? "current" : "withheld",
      unavailableMessage: frame.unavailableMessage ?? (state === void 0 || state === "ok" ? "Composition is unavailable because one or more source values are missing, invalid or negative. A complete total is required to calculate shares." : void 0),
      availabilityMessage: (state === void 0 || state === "ok") && !compositionAvailable && slices.length > 0 ? "Known values remain in the data table. The total and shares are unavailable." : void 0,
      legend: positive.map((slice, index) => ({
        key: slice.id,
        label: slice.label,
        token: seriesToken(index, slice.token)
      })),
      tableColumns,
      tableRows,
      onDrill,
      drillVerb: "see what is in this slice",
      children: /* @__PURE__ */ jsxs("div", { className: "relative h-full w-full", children: [
        /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(PieChart, { margin: { top: 0, right: 0, bottom: 0, left: 0 }, children: [
          /* @__PURE__ */ jsx(ChartPatternDefs, {}),
          /* @__PURE__ */ jsx(
            Pie,
            {
              data: positive,
              dataKey: "value",
              nameKey: "label",
              innerRadius: "58%",
              outerRadius: "88%",
              paddingAngle: 2,
              cornerRadius: 6,
              stroke: "hsl(var(--background))",
              strokeWidth: 2,
              isAnimationActive: false,
              onClick: (_sector, index) => {
                const slice = positive[index];
                if (onDrill && slice) onDrill(slice.id);
              },
              cursor: onDrill ? "pointer" : void 0,
              children: positive.map((slice, index) => /* @__PURE__ */ jsx(Cell, { fill: patternFill(seriesToken(index, slice.token)) }, slice.id))
            }
          ),
          /* @__PURE__ */ jsx(
            Tooltip,
            {
              content: ({
                active,
                payload
              }) => {
                if (!active || !payload?.length) return null;
                const entry = payload[0];
                const value = tooltipNumber(entry.value);
                return /* @__PURE__ */ jsx(
                  ChartTooltipCard,
                  {
                    label: String(entry.name ?? ""),
                    rows: [
                      {
                        key: "value",
                        name: "Value",
                        value: formatAvailableValue(value, format)
                      },
                      {
                        key: "share",
                        name: "Share",
                        value: value !== null && total !== null ? formatPercent(shareOfTotal(value, total)) : "Unavailable"
                      }
                    ],
                    footer: onDrill ? "Click to break this slice down" : void 0
                  }
                );
              }
            }
          )
        ] }) }),
        resolvedState === "ok" ? /* @__PURE__ */ jsxs("div", { className: "pointer-events-none absolute inset-0 flex flex-col items-center justify-center", children: [
          /* @__PURE__ */ jsx("span", { className: "text-muted-foreground text-2xs tracking-wider uppercase", children: centerLabel }),
          /* @__PURE__ */ jsx(
            "span",
            {
              className: "text-foreground text-base font-semibold tabular-nums",
              "data-testid": "donut-center-total",
              children: compositionAvailable ? centerValue ?? formatValue(total, format) : "Unavailable"
            }
          )
        ] }) : null
      ] })
    }
  );
}
var DIRECTION_GLYPH = {
  over: "\u25B2",
  under: "\u25BC",
  on: "\u25CF"
};
var DIRECTION_WORD = {
  over: "over budget",
  under: "under budget",
  on: "on budget"
};
var DIRECTION_WORDS_DATE = {
  over: "later than planned",
  under: "earlier than planned",
  on: "on the planned date"
};
var DIRECTION_WORDS_HEADCOUNT = {
  over: "over-staffed",
  under: "short-staffed",
  on: "fully staffed"
};
function varianceDirection(budget, actual) {
  if (!isChartNumber(budget) || !isChartNumber(actual)) return null;
  const variance = budget - actual;
  if (!isChartNumber(variance)) return null;
  if (variance < 0) return "over";
  if (variance > 0) return "under";
  return "on";
}
function VarianceBar({
  data,
  format = "currencyCompact",
  sortWorstFirst = true,
  maxRows,
  budgetLabel = "Budget",
  actualLabel = "Actual",
  directionWords = DIRECTION_WORD,
  varianceLabel = "Variance",
  categoryLabel = "Cost code",
  noBaselineNote = "No budget line recorded for this code",
  ratioLabel = "% of budget",
  noRatioText = "no budget",
  onDrill,
  state,
  height = 240,
  ...frame
}) {
  const rows = useMemo(() => {
    const computed = data.map((datum) => {
      const budget = isChartNumber(datum.budget) ? datum.budget : null;
      const actual = isChartNumber(datum.actual) ? datum.actual : null;
      const variance = budget !== null && actual !== null ? budget - actual : null;
      const ratio = budget !== null && actual !== null && budget !== 0 ? actual / budget * 100 : null;
      return {
        ...datum,
        budget,
        actual,
        variance: isChartNumber(variance) ? variance : null,
        direction: varianceDirection(budget, actual),
        percentOfBudget: isChartNumber(ratio) ? ratio : null
      };
    });
    return sortWorstFirst ? [...computed].sort((a, b) => {
      if (a.variance === null) return b.variance === null ? 0 : 1;
      if (b.variance === null) return -1;
      return a.variance - b.variance;
    }) : computed;
  }, [data, sortWorstFirst]);
  const plotted = useMemo(
    () => {
      const comparable = rows.filter((row) => row.direction !== null);
      return maxRows === void 0 ? comparable : comparable.slice(0, maxRows);
    },
    [rows, maxRows]
  );
  const byId = useMemo(() => new Map(rows.map((row) => [row.id, row])), [rows]);
  const comparableCount = rows.filter((row) => row.direction !== null).length;
  const hasMissing = comparableCount !== rows.length;
  const resolvedState = state && state !== "ok" ? state : rows.length === 0 ? "empty" : comparableCount === 0 ? "unavailable" : "ok";
  const tableColumns = [
    categoryLabel,
    budgetLabel,
    actualLabel,
    varianceLabel,
    "Direction",
    ratioLabel
  ];
  const tableRows = rows.map((row) => ({
    id: row.id,
    cells: [
      row.label,
      formatAvailableValue(row.budget, format),
      formatAvailableValue(row.actual, format),
      formatAvailableValue(row.variance, format),
      row.direction === null ? "Unavailable" : `${DIRECTION_GLYPH[row.direction]} ${directionWords[row.direction]}`,
      row.direction === null || row.percentOfBudget === null && row.budget !== 0 ? "Unavailable" : row.percentOfBudget === null ? noRatioText : formatPercent(row.percentOfBudget)
    ]
  }));
  return /* @__PURE__ */ jsx(
    ChartFrame,
    {
      ...frame,
      height,
      state: resolvedState,
      tableSourceState: state === void 0 || state === "ok" ? "current" : "withheld",
      availabilityMessage: (state === void 0 || state === "ok") && hasMissing ? "Some comparisons are unavailable. Rows without both source figures are not plotted; known figures remain in the data table." : void 0,
      legend: [
        { key: "budget", label: budgetLabel, token: "muted" },
        {
          key: "over",
          label: `${DIRECTION_GLYPH.over} ${actualLabel} ${directionWords.over}`,
          token: "critical"
        },
        {
          key: "under",
          label: `${DIRECTION_GLYPH.under} ${actualLabel} ${directionWords.under}`,
          token: "success"
        }
      ],
      tableColumns,
      tableRows,
      onDrill,
      drillVerb: "see the postings behind it",
      action: maxRows !== void 0 && comparableCount > maxRows ? /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground text-2xs", children: [
        "Worst ",
        maxRows,
        " of ",
        comparableCount,
        " available comparisons plotted"
      ] }) : frame.action,
      children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(
        BarChart,
        {
          data: plotted,
          layout: "vertical",
          margin: { top: 4, right: 12, bottom: 0, left: 0 },
          onClick: (chartState) => {
            const id = chartState?.activeLabel;
            if (onDrill && typeof id === "string") onDrill(id);
          },
          children: [
            /* @__PURE__ */ jsx(ChartPatternDefs, {}),
            /* @__PURE__ */ jsx(CartesianGrid, { stroke: CHART_CHROME.grid, strokeDasharray: "2 4", horizontal: false }),
            /* @__PURE__ */ jsx(
              XAxis,
              {
                type: "number",
                tick: AXIS_TICK,
                tickLine: false,
                axisLine: { stroke: CHART_CHROME.grid },
                tickFormatter: (value) => formatValue(value, format)
              }
            ),
            /* @__PURE__ */ jsx(
              YAxis,
              {
                type: "category",
                dataKey: "id",
                width: 150,
                tick: AXIS_TICK,
                tickLine: false,
                axisLine: false,
                interval: 0,
                tickFormatter: (id) => {
                  const row = byId.get(id);
                  return row && row.direction !== null ? `${DIRECTION_GLYPH[row.direction]} ${row.label}` : id;
                }
              }
            ),
            /* @__PURE__ */ jsx(
              Tooltip,
              {
                cursor: { fill: CHART_CHROME.cursor },
                content: ({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const row = byId.get(String(label));
                  if (!row || row.direction === null) return null;
                  return /* @__PURE__ */ jsx(
                    ChartTooltipCard,
                    {
                      label: `${DIRECTION_GLYPH[row.direction]} ${row.label}`,
                      rows: [
                        { key: "budget", name: budgetLabel, value: formatAvailableValue(row.budget, format) },
                        { key: "actual", name: actualLabel, value: formatAvailableValue(row.actual, format) },
                        {
                          key: "variance",
                          name: varianceLabel,
                          value: `${formatAvailableValue(row.variance, format)} (${directionWords[row.direction]})`
                        }
                      ],
                      footer: row.percentOfBudget === null ? row.budget === 0 ? noBaselineNote : "Ratio unavailable" : `${formatPercent(row.percentOfBudget)} of ${budgetLabel.toLowerCase()}`
                    }
                  );
                }
              }
            ),
            /* @__PURE__ */ jsx(
              Bar,
              {
                dataKey: "budget",
                name: budgetLabel,
                fill: patternFill("muted"),
                isAnimationActive: false,
                maxBarSize: 9,
                radius: [0, 5, 5, 0]
              }
            ),
            /* @__PURE__ */ jsx(
              Bar,
              {
                dataKey: "actual",
                name: actualLabel,
                isAnimationActive: false,
                maxBarSize: 9,
                radius: [0, 5, 5, 0],
                cursor: onDrill ? "pointer" : void 0,
                children: plotted.map((row) => /* @__PURE__ */ jsx(
                  Cell,
                  {
                    fill: row.direction === "over" ? patternFill("critical") : row.direction === "under" ? patternFill("success") : patternFill("info"),
                    stroke: row.direction === "over" ? CHART_COLOR.critical : CHART_COLOR.success,
                    strokeWidth: row.direction === "over" ? 1 : 0
                  },
                  row.id
                ))
              }
            )
          ]
        }
      ) })
    }
  );
}
function Sparkline({
  values,
  label,
  token = "primary",
  height = 32,
  className
}) {
  const rows = useMemo(
    () => values.map((value, index) => ({ index, value: isChartNumber(value) ? value : null })),
    [values]
  );
  const measured = values.filter(isChartNumber);
  if (measured.length < 2) {
    return /* @__PURE__ */ jsxs(
      "p",
      {
        className: cn("text-muted-foreground text-2xs", className),
        "data-testid": "sparkline-insufficient",
        children: [
          measured.length === 0 ? "No history recorded" : "Only one period recorded",
          " \u2014 no trend to draw"
        ]
      }
    );
  }
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("min-w-0", className),
      style: { height },
      role: "img",
      "aria-label": `${label}: ${measured.length} measured periods, from ${measured[0]} to ${measured[measured.length - 1]}`,
      children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsx(LineChart, { data: rows, margin: { top: 3, right: 1, bottom: 3, left: 1 }, children: /* @__PURE__ */ jsx(
        Line,
        {
          type: "monotone",
          dataKey: "value",
          className: `hx-glow-${token}`,
          stroke: CHART_COLOR[token],
          strokeWidth: 2,
          strokeLinecap: "round",
          dot: false,
          connectNulls: false,
          isAnimationActive: false
        }
      ) }) })
    }
  );
}
function FunnelChart({
  stages,
  format = "count",
  onDrill,
  state,
  height = 220,
  ...frame
}) {
  const plotted = useMemo(() => stages.map((stage) => ({ ...stage, value: isChartNumber(stage.value) && stage.value >= 0 ? stage.value : null })), [stages]);
  const entry = plotted[0]?.value ?? null;
  const byId = useMemo(() => new Map(plotted.map((stage) => [stage.id, stage])), [plotted]);
  const hasKnown = plotted.some((stage) => stage.value !== null);
  const hasMissing = plotted.some((stage) => stage.value === null);
  const ratioText = (value, denominator, noVolumeText) => {
    if (value === null || denominator === null) return "Unavailable";
    if (denominator === 0) return noVolumeText;
    const ratio = shareOfTotal(value, denominator);
    return isChartNumber(ratio) ? formatPercent(ratio) : "Unavailable";
  };
  const resolvedState = state && state !== "ok" ? state : stages.length === 0 || stages.every((stage) => stage.value === 0) ? "empty" : hasKnown ? "ok" : "unavailable";
  const tableColumns = ["Stage", "Count", "Share of first stage", "Step conversion"];
  const tableRows = stages.map((stage, index) => {
    const previous = index === 0 ? null : plotted[index - 1].value;
    const value = plotted[index].value;
    return {
      id: stage.id,
      cells: [
        `${index + 1}. ${stage.label}`,
        formatAvailableValue(stage.value, format),
        ratioText(value, entry, "no entries"),
        index === 0 ? "\u2014" : ratioText(value, previous, "no prior stage volume")
      ]
    };
  });
  return /* @__PURE__ */ jsx(
    ChartFrame,
    {
      ...frame,
      height,
      state: resolvedState,
      tableSourceState: state === void 0 || state === "ok" ? "current" : "withheld",
      availabilityMessage: (state === void 0 || state === "ok") && hasMissing ? "Some source volumes are unavailable or invalid. Only known nonnegative volumes are plotted; conversions require both source volumes." : void 0,
      tableColumns,
      tableRows,
      onDrill,
      drillVerb: "see what is sitting at this stage",
      children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(
        BarChart,
        {
          data: plotted,
          layout: "vertical",
          margin: { top: 4, right: 12, bottom: 0, left: 0 },
          onClick: (chartState) => {
            const id = chartState?.activeLabel;
            if (onDrill && typeof id === "string") onDrill(id);
          },
          children: [
            /* @__PURE__ */ jsx(ChartPatternDefs, {}),
            /* @__PURE__ */ jsx(XAxis, { type: "number", hide: true }),
            /* @__PURE__ */ jsx(
              YAxis,
              {
                type: "category",
                dataKey: "id",
                width: 140,
                tick: AXIS_TICK,
                tickLine: false,
                axisLine: false,
                interval: 0,
                tickFormatter: (id) => {
                  const index = stages.findIndex((stage) => stage.id === id);
                  return `${index + 1}. ${byId.get(id)?.label ?? id}`;
                }
              }
            ),
            /* @__PURE__ */ jsx(
              Tooltip,
              {
                cursor: { fill: "hsl(var(--muted) / 0.45)" },
                content: ({
                  active,
                  payload,
                  label
                }) => {
                  if (!active || !payload?.length) return null;
                  const stage = byId.get(String(label));
                  if (!stage) return null;
                  return /* @__PURE__ */ jsx(
                    ChartTooltipCard,
                    {
                      label: stage.label,
                      rows: [
                        { key: "count", name: "At this stage", value: formatAvailableValue(stage.value, format) },
                        {
                          key: "share",
                          name: "Of first stage",
                          value: ratioText(stage.value, entry, "no entries")
                        }
                      ],
                      footer: onDrill ? "Click to list them" : void 0
                    }
                  );
                }
              }
            ),
            /* @__PURE__ */ jsx(
              Bar,
              {
                dataKey: "value",
                name: "Stage volume",
                isAnimationActive: false,
                maxBarSize: 22,
                radius: [0, 10, 10, 0],
                cursor: onDrill ? "pointer" : void 0,
                children: stages.map((stage, index) => /* @__PURE__ */ jsx(Cell, { fill: patternFill(seriesToken(index)) }, stage.id))
              }
            )
          ]
        }
      ) })
    }
  );
}
var STANDARD_AGING_BUCKETS = ["0\u201330", "31\u201360", "61\u201390", "90+"];
var AGE_RAMP = ["success", "info", "warning", "critical"];
function AgingBars({
  buckets,
  format = "currencyCompact",
  valueLabel = "Amount",
  onDrill,
  state,
  height = 200,
  ...frame
}) {
  const byId = useMemo(() => new Map(buckets.map((bucket) => [bucket.id, bucket])), [buckets]);
  const plotted = buckets.map((bucket) => ({ ...bucket, value: isChartNumber(bucket.value) ? bucket.value : null }));
  const hasKnownValue = buckets.some((bucket) => isChartNumber(bucket.value));
  const hasMissing = buckets.some((bucket) => !isChartNumber(bucket.value));
  const allZero = buckets.length > 0 && buckets.every((bucket) => bucket.value === 0);
  const resolvedState = state && state !== "ok" ? state : buckets.length === 0 || allZero ? "empty" : hasKnownValue ? "ok" : "unavailable";
  const countText = (count) => count === void 0 ? "-" : isChartNumber(count) && Number.isInteger(count) && count >= 0 ? formatCount(count) : "Unavailable";
  const tableColumns = ["Age bucket", valueLabel, "Records"];
  const tableRows = buckets.map((bucket) => ({
    id: bucket.id,
    cells: [
      bucket.label,
      formatAvailableValue(bucket.value, format),
      countText(bucket.count)
    ]
  }));
  return /* @__PURE__ */ jsx(
    ChartFrame,
    {
      ...frame,
      height,
      state: resolvedState,
      tableSourceState: state === void 0 || state === "ok" ? "current" : "withheld",
      tableColumns,
      tableRows,
      onDrill,
      drillVerb: "list what is sitting in this bucket",
      description: frame.description ?? "Buckets run youngest to oldest, left to right - the money on the right is the exposure.",
      availabilityMessage: (state === void 0 || state === "ok") && hasMissing ? "Some bucket amounts are unavailable. Only known amounts are plotted; missing amounts are not zero." : void 0,
      children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(
        BarChart,
        {
          data: plotted,
          margin: { top: 6, right: 8, bottom: 0, left: 0 },
          onClick: (chartState) => {
            const id = chartState?.activeLabel;
            if (onDrill && typeof id === "string") onDrill(id);
          },
          children: [
            /* @__PURE__ */ jsx(ChartPatternDefs, {}),
            /* @__PURE__ */ jsx(CartesianGrid, { stroke: CHART_CHROME.grid, strokeDasharray: "2 4", vertical: false }),
            /* @__PURE__ */ jsx(
              XAxis,
              {
                dataKey: "id",
                tick: AXIS_TICK,
                tickLine: false,
                axisLine: { stroke: CHART_CHROME.grid },
                interval: 0,
                tickFormatter: (id) => byId.get(id)?.label ?? id
              }
            ),
            /* @__PURE__ */ jsx(
              YAxis,
              {
                tick: AXIS_TICK,
                tickLine: false,
                axisLine: false,
                width: 62,
                tickFormatter: (value) => formatValue(value, format)
              }
            ),
            /* @__PURE__ */ jsx(
              Tooltip,
              {
                cursor: { fill: CHART_CHROME.cursor },
                content: ({
                  active,
                  payload,
                  label
                }) => {
                  if (!active || !payload?.length) return null;
                  const bucket = byId.get(String(label));
                  if (!bucket) return null;
                  return /* @__PURE__ */ jsx(
                    ChartTooltipCard,
                    {
                      label: bucket.label,
                      rows: [
                        { key: "value", name: valueLabel, value: formatAvailableValue(bucket.value, format) },
                        ...bucket.count === void 0 ? [] : [
                          {
                            key: "count",
                            name: "Records",
                            value: countText(bucket.count)
                          }
                        ]
                      ],
                      footer: onDrill ? "Click to list them" : void 0
                    }
                  );
                }
              }
            ),
            /* @__PURE__ */ jsx(
              Bar,
              {
                dataKey: "value",
                name: valueLabel,
                isAnimationActive: false,
                maxBarSize: 56,
                radius: [8, 8, 0, 0],
                cursor: onDrill ? "pointer" : void 0,
                children: buckets.map((bucket, index) => /* @__PURE__ */ jsx(
                  Cell,
                  {
                    fill: patternFill(AGE_RAMP[Math.min(index, AGE_RAMP.length - 1)])
                  },
                  bucket.id
                ))
              }
            )
          ]
        }
      ) })
    }
  );
}
function GaugeArc({
  value,
  max = 100,
  token = "primary",
  caption,
  measureLabel,
  state,
  height = 160,
  ...frame
}) {
  const validScale = isChartNumber(max) && max > 0;
  const measurable = isChartNumber(value) && validScale;
  const resolvedState = state && state !== "ok" ? state : measurable ? "ok" : value === null && validScale ? "empty" : "unavailable";
  const clamped = measurable ? Math.max(0, Math.min(value, max)) : null;
  return /* @__PURE__ */ jsx(
    ChartFrame,
    {
      ...frame,
      height,
      state: resolvedState,
      tableSourceState: state === void 0 || state === "ok" ? "current" : "withheld",
      emptyMessage: frame.emptyMessage ?? (state === void 0 || state === "ok" ? "This ratio has no denominator on the stated basis, so no arc has been drawn." : void 0),
      tableColumns: ["Measure", "Value", "Scale"],
      tableRows: [
        {
          id: "gauge",
          cells: [
            measureLabel ?? frame.title,
            isChartNumber(value) ? formatPercent(value, 1) : "not measurable",
            validScale ? `0 to ${max}` : "Unavailable"
          ]
        }
      ],
      children: /* @__PURE__ */ jsxs("div", { className: "relative h-full w-full", children: [
        /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(
          RadialBarChart,
          {
            data: [{ name: frame.title, value: clamped }],
            innerRadius: "68%",
            outerRadius: "100%",
            startAngle: 210,
            endAngle: -30,
            margin: { top: 0, right: 0, bottom: 0, left: 0 },
            children: [
              /* @__PURE__ */ jsx(PolarAngleAxis, { type: "number", domain: [0, max], tick: false, axisLine: false }),
              /* @__PURE__ */ jsx(
                RadialBar,
                {
                  dataKey: "value",
                  background: { fill: "hsl(0 0% 100% / 0.07)" },
                  cornerRadius: 10,
                  className: `hx-glow-${token}`,
                  fill: CHART_COLOR[token],
                  isAnimationActive: false
                }
              )
            ]
          }
        ) }),
        /* @__PURE__ */ jsxs("div", { className: "pointer-events-none absolute inset-0 flex flex-col items-center justify-center pt-3", children: [
          /* @__PURE__ */ jsx(
            "span",
            {
              className: "hx-gradient-text text-2xl font-semibold tabular-nums",
              "data-testid": "gauge-value",
              children: measurable ? formatPercent(value, 1) : "\u2014"
            }
          ),
          caption ? /* @__PURE__ */ jsx("span", { className: "text-muted-foreground max-w-[85%] text-center text-2xs", children: caption }) : null,
          /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground text-2xs", children: [
            "scale 0\u2013",
            max
          ] })
        ] })
      ] })
    }
  );
}

export { AXIS_TICK, AgingBars, BreakdownBar, CHART_CHROME, CHART_COLOR, CHART_SERIES_ORDER, ChartDrillDown, ChartFrame, ChartPatternDefs, ChartTooltipCard, DIRECTION_GLYPH, DIRECTION_WORD, DIRECTION_WORDS_DATE, DIRECTION_WORDS_HEADCOUNT, FunnelChart, GaugeArc, STANDARD_AGING_BUCKETS, ShareDonut, Sparkline, TrendChart, VarianceBar, areaFill, barFill, cohortHasRecords, findCohort, formatCount, formatCurrency, formatCurrencyCompact, formatHours, formatMonthLabel, formatPercent, formatShortDate, formatValue, parseMoney, patternFill, patternId, seriesColor, seriesToken, shareOfTotal, useReducedMotion, varianceDirection };
//# sourceMappingURL=charts.mjs.map
//# sourceMappingURL=charts.mjs.map